import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, LayoutGrid, List as ListIcon, Download } from "lucide-react";
import { toast } from "sonner";
import LeadDetail from "../components/crm/LeadDetail";
import { clientStageBadgeClass, daysSince } from "../components/crm/constants";

/**
 * Clients page — delivery pipeline for paying clients.
 * Mirrors the Leads page but scoped to `kind=client` and uses the client stage list.
 */
export default function Clients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ref, setRef] = useState({ stages: [], client_stages: [], sources: [], users: [], packages: [], lost_reasons: [] });
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [packageFilter, setPackageFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    const params = { kind: "client" };
    if (q) params.q = q;
    if (stageFilter !== "all") params.stage = stageFilter;
    if (ownerFilter !== "all") params.owner_id = ownerFilter;
    api.get("/crm/leads", { params }).then((r) => {
      const rows = r.data;
      setClients(packageFilter === "all" ? rows : rows.filter((c) => c.package_type === packageFilter));
    }).catch(() => setClients([])).finally(() => setLoading(false));
  }, [q, stageFilter, ownerFilter, packageFilter]);

  useEffect(() => { api.get("/crm/reference").then((r) => setRef(r.data)); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Auto-open a client when ?lead=<id> is present (from Notification bell / dashboard).
  useEffect(() => {
    const lid = searchParams.get("lead");
    if (!lid || selected?.id === lid) return;
    api.get(`/crm/leads/${lid}`).then((r) => setSelected(r.data)).catch(() => {});
  }, [searchParams, selected?.id]);

  const stages = ref.client_stages || [];
  const usersById = useMemo(() => {
    const m = {}; (ref.users || []).forEach((u) => (m[u.id] = u)); return m;
  }, [ref]);

  const byStage = useMemo(() => {
    const m = {}; stages.forEach((s) => (m[s.key] = []));
    clients.forEach((l) => { if (m[l.stage]) m[l.stage].push(l); });
    return m;
  }, [clients, stages]);

  async function onCardDrop(clientId, newStage) {
    const client = clients.find((l) => l.id === clientId);
    if (!client || client.stage === newStage) return;
    try {
      await api.post(`/crm/leads/${clientId}/stage`, { stage: newStage });
      toast.success("Stage updated");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot update");
    }
  }

  async function exportCsv() {
    try {
      const params = { kind: "client" };
      if (q) params.q = q;
      if (stageFilter !== "all") params.stage = stageFilter;
      if (ownerFilter !== "all") params.owner_id = ownerFilter;
      const r = await api.get("/crm/leads/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${clients.length} client${clients.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    }
  }

  function packageBadgeClass(pkg) {
    if (pkg === "full_package") return "bg-[#0D1B2A] text-white";
    if (pkg === "registration_only") return "bg-[#1A7F6E] text-white";
    return "bg-slate-200 text-slate-600";
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Delivery pipeline</p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Clients</h1>
          <p className="text-sm text-slate-500 mt-1">Onboarding · assessment · documents · audit. Everything happens here.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button type="button" data-testid="view-kanban" onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-[#0D1B2A] text-white" : "bg-white text-slate-600"}`}>
              <LayoutGrid className="w-4 h-4" />Kanban
            </button>
            <button type="button" data-testid="view-list" onClick={() => setView("list")}
              className={`px-3 py-1.5 text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#0D1B2A] text-white" : "bg-white text-slate-600"}`}>
              <ListIcon className="w-4 h-4" />List
            </button>
          </div>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organisation, contact, email…" className="pl-9" data-testid="client-search" />
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-44" data-testid="filter-stage"><SelectValue placeholder="All stages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={packageFilter} onValueChange={setPackageFilter}>
            <SelectTrigger className="w-44" data-testid="filter-package"><SelectValue placeholder="All packages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All packages</SelectItem>
              {(ref.packages || []).map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-44" data-testid="filter-owner"><SelectValue placeholder="All consultants" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All consultants</SelectItem>
              {(ref.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
      )}
      {!loading && clients.length === 0 && (
        <Card className="border-dashed border-slate-300 shadow-none">
          <CardContent className="p-10 text-center">
            <p className="text-sm text-slate-500 mb-4">No clients match your filters yet.</p>
            <p className="text-xs text-slate-400 mb-4">Convert a paying lead from the Leads page to add your first client.</p>
            <Button variant="outline" onClick={() => nav("/crm")}>Go to Leads</Button>
          </CardContent>
        </Card>
      )}
      {!loading && clients.length > 0 && view === "kanban" && (
        <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-2">
          {stages.map((s) => (
            <div key={s.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onCardDrop(e.dataTransfer.getData("text/plain"), s.key)}
              className="bg-slate-100 rounded-lg p-3 min-h-[300px]" data-testid={`col-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{s.name}</span>
                <span className="text-xs font-semibold text-[#0D1B2A]">{byStage[s.key]?.length || 0}</span>
              </div>
              <div className="space-y-2">
                {(byStage[s.key] || []).map((l) => (
                  <button type="button" key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)}
                    onClick={() => setSelected(l)}
                    className="w-full text-left bg-white border border-slate-200 rounded-md p-3 shadow-sm cursor-pointer hover:border-[#1A7F6E]"
                    data-testid={`card-${l.id}`}>
                    <p className="text-sm font-semibold text-[#0D1B2A] truncate">{l.org_name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.contact_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${packageBadgeClass(l.package_type)}`}>
                        {l.package_type === "full_package" ? "Full" : l.package_type === "registration_only" ? "Reg only" : "—"}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {daysSince(l.stage_updated_at)}d · {usersById[l.owner_id]?.name?.split(" ")[0] || "—"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && clients.length > 0 && view === "list" && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-3 px-2">Organisation</th>
                  <th className="text-left py-3 px-2">Contact</th>
                  <th className="text-left py-3 px-2">Stage</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Package</th>
                  <th className="text-left py-3 px-2 hidden md:table-cell">Owner</th>
                  <th className="text-left py-3 px-2 hidden sm:table-cell">Days in stage</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(l)} data-testid={`row-${l.id}`}>
                    <td className="py-3 px-2 font-medium text-[#0D1B2A]">{l.org_name}</td>
                    <td className="py-3 px-2 text-slate-600">
                      {l.contact_name}<br />
                      <span className="text-xs text-slate-400">{l.contact_email}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${clientStageBadgeClass(l.stage)}`}>
                        {stages.find((s) => s.key === l.stage)?.name || l.stage}
                      </span>
                    </td>
                    <td className="py-3 px-2 hidden md:table-cell text-slate-600">
                      {l.package_type === "full_package" ? "Full Package" : l.package_type === "registration_only" ? "Only Registration" : "—"}
                    </td>
                    <td className="py-3 px-2 hidden md:table-cell text-slate-500">{usersById[l.owner_id]?.name || "—"}</td>
                    <td className="py-3 px-2 hidden sm:table-cell text-slate-500">{daysSince(l.stage_updated_at)}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {selected && (
        <LeadDetail
          lead={selected}
          me={user}
          ref_={ref}
          onClose={() => { setSelected(null); if (searchParams.get("lead")) setSearchParams({}); }}
          onChanged={refresh}
          onConvert={(l) => nav(`/new?lead=${l.id}`)}
        />
      )}
    </div>
  );
}
